// MessagingPanel.jsx — Style Messenger
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageCircle, ChevronLeft, Check, CheckCheck, Search, Trash2, Phone } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

// ── HELPERS ─────────────────────────────────────────────────
const getName = (contact) => {
  if (!contact) return 'Utilisateur';
  const p = contact.client || contact.prestataire;
  return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : 'Utilisateur';
};

const getPhoto = (contact) => {
  if (!contact) return null;
  return (contact.client || contact.prestataire)?.profilePhotoUrl || null;
};

const formatTime = (date) => {
  const d = new Date(date), now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const diff = now - d;
  if (diff < 7 * 24 * 60 * 60 * 1000)
    return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const formatDateSep = (date) =>
  new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

const isSameDay = (a, b) =>
  new Date(a).toDateString() === new Date(b).toDateString();

const formatDuration = (secs) => {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ── AVATAR ──────────────────────────────────────────────────
function Avatar({ contact, size = 42 }) {
  const photo = getPhoto(contact);
  const name  = getName(contact);
  const isPrest = contact?.role === 'PRESTATAIRE';
  const bg = isPrest
    ? 'linear-gradient(135deg, #FF6B00, #CC4400)'
    : 'linear-gradient(135deg, #1A3B5D, #0D5B8E)';

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: bg, overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      color: 'white', fontWeight: 700, fontSize: size * 0.38,
      fontFamily: 'var(--font-cond)',
      border: `2px solid ${isPrest ? 'rgba(255,107,0,0.4)' : 'rgba(26,59,93,0.3)'}`,
    }}>
      {photo
        ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : name.charAt(0).toUpperCase()
      }
    </div>
  );
}

// ── BULLE DE MESSAGE ─────────────────────────────────────────
function Bubble({ msg, isMine, showAvatar, contact, currentUser, onDelete }) {
  const [hovered,    setHovered]    = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const handleDelete = () => {
    if (!confirmDel) { setConfirmDel(true); setTimeout(() => setConfirmDel(false), 3000); return; }
    onDelete(msg.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.15 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDel(false); }}
      style={{
        display: 'flex',
        flexDirection: isMine ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: '6px',
        marginBottom: '2px',
        padding: '0 8px',
      }}
    >
      <div style={{ width: 32, flexShrink: 0, marginBottom: '2px' }}>
        {!isMine && showAvatar && <Avatar contact={contact} size={30} />}
      </div>

      <div style={{
        maxWidth: '65%', display: 'flex', flexDirection: 'column',
        alignItems: isMine ? 'flex-end' : 'flex-start', gap: '2px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexDirection: isMine ? 'row-reverse' : 'row' }}>

          {/* BULLE */}
          <div style={{
            padding: '9px 14px',
            borderRadius: isMine ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
            background: isMine ? 'linear-gradient(135deg, #FF6B00, #E05500)' : 'white',
            color: isMine ? 'white' : '#111',
            fontSize: '0.9rem', lineHeight: 1.55, wordBreak: 'break-word',
            boxShadow: isMine ? '0 4px 16px rgba(255,107,0,0.3)' : '0 2px 8px rgba(0,0,0,0.09)',
            border: isMine ? 'none' : '1px solid rgba(0,0,0,0.07)',
          }}>
            {msg.content}
          </div>

          {/* Bouton supprimer */}
          <AnimatePresence>
            {hovered && (
              <motion.button
                initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                onClick={handleDelete}
                style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  border: 'none', cursor: 'pointer',
                  background: confirmDel ? '#EF4444' : 'rgba(0,0,0,0.07)',
                  color: confirmDel ? 'white' : 'rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Trash2 size={11} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Timestamp + lu */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '3px', padding: '0 4px',
          flexDirection: isMine ? 'row-reverse' : 'row',
        }}>
          <span style={{ fontSize: '0.62rem', color: 'rgba(0,0,0,0.32)' }}>{formatTime(msg.createdAt)}</span>
          {isMine && (msg.isRead ? <CheckCheck size={12} color="#10B981" /> : <Check size={12} color="rgba(0,0,0,0.28)" />)}
          {confirmDel && <span style={{ fontSize: '0.58rem', color: '#EF4444', fontWeight: 600 }}>Confirmer ?</span>}
        </div>
      </div>
    </motion.div>
  );
}

// ── THREAD (panneau droit) ───────────────────────────────────
function ChatThread({ contactId, contact, currentUser, onBack }) {
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [sending,   setSending]   = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [atBottom,  setAtBottom]  = useState(true);
  const [showCallMenu, setShowCallMenu] = useState(false);

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const pollRef   = useRef(null);
  const scrollRef = useRef(null);
  const prevLen   = useRef(0);
  const isMobile  = window.innerWidth < 768;

  const scrollToBottom = (behavior = 'smooth') =>
    bottomRef.current?.scrollIntoView({ behavior });

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 60);
  };

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/thread/${contactId}`);
      setMessages(data);
    } catch {}
    setLoading(false);
  }, [contactId]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    prevLen.current = 0;
    setAtBottom(true);
    setShowCallMenu(false);
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    if (loading) return;
    const firstLoad = prevLen.current === 0 && messages.length > 0;
    if (firstLoad) scrollToBottom('instant');
    else if (atBottom && messages.length > prevLen.current) scrollToBottom('smooth');
    prevLen.current = messages.length;
  }, [messages, loading, atBottom]);

  useEffect(() => { inputRef.current?.focus(); }, [contactId]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    const content = input.trim();
    const optimistic = {
      id: `tmp-${Date.now()}`, type: 'TEXT',
      senderId: currentUser?.id, receiverId: contactId,
      content, createdAt: new Date().toISOString(), isRead: false,
    };
    setMessages(prev => [...prev, optimistic]);
    setInput('');
    setTimeout(() => scrollToBottom('smooth'), 50);
    try {
      await api.post('/messages', { receiverId: contactId, content });
      await fetchMessages();
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInput(content);
    } finally { setSending(false); }
  };

  const deleteMessage = async (id) => {
    if (String(id).startsWith('tmp-')) return;
    setMessages(prev => prev.filter(m => m.id !== id));
    try {
      await api.delete(`/messages/${id}`);
    } catch (err) {
      console.error('delete error:', err.response?.data || err.message);
      fetchMessages();
    }
  };


  const renderMessages = () => {
    const els = [];
    messages.forEach((msg, i) => {
      const prev = messages[i - 1];
      if (!prev || !isSameDay(prev.createdAt, msg.createdAt)) {
        els.push(
          <div key={`sep-${msg.id}`} style={{ textAlign: 'center', margin: '16px 0 8px' }}>
            <span style={{ fontSize: '0.67rem', color: 'rgba(0,0,0,0.38)', background: 'rgba(0,0,0,0.06)', padding: '3px 12px', borderRadius: '100px', fontWeight: 500 }}>
              {formatDateSep(msg.createdAt)}
            </span>
          </div>
        );
      }
      const isMine     = msg.senderId === currentUser?.id;
      const next       = messages[i + 1];
      const showAvatar = !isMine && (!next || next.senderId !== msg.senderId);
      els.push(
        <Bubble key={msg.id} msg={msg} isMine={isMine} showAvatar={showAvatar}
          contact={contact} currentUser={currentUser} onDelete={deleteMessage} />
      );
    });
    return els;
  };

  const contactName = getName(contact);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F0F2F5' }}>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: 'white', borderBottom: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', flexShrink: 0 }}>
        {isMobile && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue-dark)', padding: '4px', display: 'flex' }}>
            <ChevronLeft size={22} />
          </button>
        )}
        <div style={{ position: 'relative' }}>
          <Avatar contact={contact} size={40} />
          <div style={{ position: 'absolute', bottom: 1, right: 1, width: 11, height: 11, borderRadius: '50%', background: '#10B981', border: '2px solid white' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', color: '#111', lineHeight: 1.2 }}>{contactName}</div>
          <div style={{ fontSize: '0.67rem', color: '#10B981', fontWeight: 600 }}>● En ligne</div>
        </div>
        <span style={{ fontSize: '0.65rem', fontFamily: 'var(--font-cond)', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: contact?.role === 'PRESTATAIRE' ? 'rgba(255,107,0,0.12)' : 'rgba(26,59,93,0.08)', color: contact?.role === 'PRESTATAIRE' ? '#FF6B00' : '#1A3B5D' }}>
          {contact?.role === 'PRESTATAIRE' ? '🔧 Prestataire' : '👤 Client'}
        </span>

        {/* ── BOUTON APPEL ── */}
        <div style={{ position: 'relative' }}>
          <motion.button
            onClick={() => setShowCallMenu(p => !p)}
            whileTap={{ scale: 0.88 }}
            style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #22C55E, #16A34A)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 12px rgba(34,197,94,0.4)',
            }}
            title="Appeler"
          >
            <Phone size={16} color="white" />
          </motion.button>

          <AnimatePresence>
            {showCallMenu && (() => {
              const phone = contact?.prestataire?.phone || contact?.client?.phone || contact?.phone || '';
              const whatsapp = contact?.prestataire?.whatsapp || contact?.whatsapp || phone;
              return (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.85, y: -8 }}
                  style={{
                    position: 'absolute', top: '110%', right: 0, zIndex: 100,
                    background: 'white', borderRadius: '14px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    border: '1px solid rgba(0,0,0,0.07)',
                    overflow: 'hidden', minWidth: '180px',
                  }}
                >
                  <div style={{ padding: '8px 14px 6px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <span style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Contacter {contactName.split(' ')[0]}
                    </span>
                  </div>
                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/${(whatsapp || '').replace(/\D/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => setShowCallMenu(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', textDecoration: 'none', color: '#111',
                      fontSize: '0.88rem', fontWeight: 600,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '1.2rem' }}>💬</span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#16A34A' }}>WhatsApp</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.4)' }}>{whatsapp || 'Non renseigné'}</div>
                    </div>
                  </a>
                  {/* Appel téléphone */}
                  <a
                    href={`tel:${(phone || '').replace(/\D/g, '')}`}
                    onClick={() => setShowCallMenu(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', textDecoration: 'none', color: '#111',
                      fontSize: '0.88rem', fontWeight: 600,
                      borderTop: '1px solid rgba(0,0,0,0.05)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '1.2rem' }}>📞</span>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1A3B5D' }}>Appel téléphone</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.4)' }}>{phone || 'Non renseigné'}</div>
                    </div>
                  </a>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>

      {/* ── MESSAGES ── */}
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: '8px 0 4px', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ fontSize: '2rem' }}>⚙️</motion.div>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
            <Avatar contact={contact} size={64} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: '#111', fontSize: '1rem' }}>{contactName}</div>
              <p style={{ color: 'rgba(0,0,0,0.38)', fontSize: '0.82rem', marginTop: '4px' }}>Commencez la conversation !</p>
            </div>
          </div>
        ) : (
          <>{renderMessages()}<div style={{ height: 8 }} /></>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Bouton scroll en bas */}
      <AnimatePresence>
        {!atBottom && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            style={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
            <button onClick={() => { setAtBottom(true); scrollToBottom(); }}
              style={{ background: 'var(--blue-dark)', color: 'white', border: 'none', borderRadius: '100px', padding: '6px 16px', fontSize: '0.73rem', fontFamily: 'var(--font-cond)', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.28)' }}>
              ↓ Nouveaux messages
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── ZONE DE SAISIE ── */}
      <div style={{ padding: '8px 12px 10px', background: 'white', borderTop: '1px solid rgba(0,0,0,0.07)', flexShrink: 0 }}>
        <motion.div key="text" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', background: '#F0F2F5', borderRadius: '24px', padding: '6px 6px 6px 14px' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={`Écrire à ${contactName}...`}
                rows={1}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: '0.9rem', color: '#111', resize: 'none', fontFamily: 'inherit', lineHeight: 1.5, paddingTop: '4px', maxHeight: '100px', overflowY: 'auto' }}
              />

              {/* Bouton envoyer */}
              {input.trim() && (
                <motion.button
                  onClick={sendMessage}
                  disabled={sending}
                  whileTap={{ scale: 0.85 }}
                  style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #FF6B00, #CC4400)', border: 'none', cursor: sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(255,107,0,0.35)', transition: 'all 0.2s' }}
                >
                  <Send size={15} color="white" style={{ marginLeft: '2px' }} />
                </motion.button>
              )}
            </motion.div>
        <p style={{ fontSize: '0.6rem', color: 'rgba(0,0,0,0.22)', textAlign: 'center', marginTop: '4px' }}>
          Entrée pour envoyer · Maj+Entrée pour sauter une ligne
        </p>
      </div>
    </div>
  );
}

// ── LISTE DES CONVERSATIONS (panneau gauche) ─────────────────
function ConversationList({ conversations, currentUserId, onSelect, selectedId, search, setSearch }) {
  const filtered = conversations.filter(conv =>
    getName(conv.contact).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F0F2F5', borderRadius: '20px', padding: '6px 12px' }}>
          <Search size={14} color="rgba(0,0,0,0.35)" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: '0.83rem', color: '#111' }} />
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60%', gap: '12px', padding: '2rem' }}>
            <MessageCircle size={36} color="rgba(0,0,0,0.1)" />
            <p style={{ color: 'rgba(0,0,0,0.35)', textAlign: 'center', fontSize: '0.82rem' }}>
              {search ? 'Aucun résultat' : 'Aucune conversation'}
            </p>
          </div>
        ) : filtered.map((conv, i) => {
          const isSelected = selectedId === conv.contactId;
          const hasUnread  = conv.unreadCount > 0;
          const isPrest    = conv.contact?.role === 'PRESTATAIRE';
          const accent     = isPrest ? '#FF6B00' : '#1A3B5D';
          const lastText = conv.lastMessage?.senderId === currentUserId
              ? `Vous : ${conv.lastMessage.content}`
              : conv.lastMessage?.content || '';

          return (
            <motion.button key={conv.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => onSelect(conv)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: 'none', cursor: 'pointer', textAlign: 'left', background: isSelected ? `${accent}12` : 'transparent', borderLeft: `3px solid ${isSelected ? accent : 'transparent'}`, borderBottom: '1px solid rgba(0,0,0,0.04)', transition: 'background 0.15s' }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <Avatar contact={conv.contact} size={46} />
                {hasUnread && (
                  <div style={{ position: 'absolute', top: -2, right: -2, minWidth: 18, height: 18, borderRadius: '100px', background: '#FF6B00', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', color: 'white', fontWeight: 700, padding: '0 3px' }}>
                    {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                  <span style={{ fontFamily: 'var(--font-cond)', fontWeight: hasUnread ? 800 : 600, fontSize: '0.9rem', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '130px' }}>
                    {getName(conv.contact)}
                  </span>
                  <span style={{ fontSize: '0.6rem', color: 'rgba(0,0,0,0.3)', flexShrink: 0, marginLeft: '4px' }}>
                    {conv.lastMessage?.createdAt ? formatTime(conv.lastMessage.createdAt) : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {conv.lastMessage?.senderId === currentUserId && (
                    conv.lastMessage.isRead ? <CheckCheck size={11} color="#10B981" style={{ flexShrink: 0 }} /> : <Check size={11} color="rgba(0,0,0,0.3)" style={{ flexShrink: 0 }} />
                  )}
                  <p style={{ color: hasUnread ? '#111' : 'rgba(0,0,0,0.42)', fontSize: '0.75rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: hasUnread ? 600 : 400, flex: 1 }}>
                    {lastText}
                  </p>
                  <span style={{ fontSize: '0.58rem', flexShrink: 0, background: isPrest ? 'rgba(255,107,0,0.1)' : 'rgba(26,59,93,0.08)', color: isPrest ? '#FF6B00' : '#1A3B5D', padding: '1px 5px', borderRadius: 4, fontFamily: 'var(--font-cond)', fontWeight: 700 }}>
                    {isPrest ? '🔧' : '👤'}
                  </span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ── COMPOSANT PRINCIPAL ──────────────────────────────────────
export default function MessagingPanel({ initialContact = null }) {
  const { user }                          = useAuthStore();
  const [conversations, setConversations] = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [search,        setSearch]        = useState('');
  const [totalUnread,   setTotalUnread]   = useState(0);
  const pollRef  = useRef(null);
  const isMobile = window.innerWidth < 768;

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/conversations');
      setConversations(data);
      setTotalUnread(data.reduce((s, c) => s + (c.unreadCount || 0), 0));
    } catch {}
  }, []);

  useEffect(() => {
    fetchConversations();
    pollRef.current = setInterval(fetchConversations, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations]);

  useEffect(() => {
    if (!initialContact) return;
    setSelected({ contactId: initialContact.id, contact: initialContact });
  }, [initialContact]);

  const handleSelect = (conv) => {
    setSelected({ contactId: conv.contactId, contact: conv.contact });
    setTimeout(fetchConversations, 600);
  };

  const showList   = !isMobile || !selected;
  const showThread = !isMobile || !!selected;

  return (
    <div style={{ background: 'white', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '16px', overflow: 'hidden', height: '680px', display: 'flex', boxShadow: '0 8px 40px rgba(0,0,0,0.12)', position: 'relative' }}>

      {showList && (
        <div style={{ width: isMobile ? '100%' : '300px', flexShrink: 0, borderRight: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', background: 'white' }}>
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageCircle size={20} color="#FF6B00" />
              <span style={{ fontFamily: 'var(--font-display)', color: '#111', fontSize: '1.05rem', letterSpacing: '1px' }}>MESSAGES</span>
              {totalUnread > 0 && (
                <span style={{ background: '#FF6B00', color: 'white', borderRadius: '100px', padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700 }}>{totalUnread}</span>
              )}
            </div>
          </div>
          <ConversationList conversations={conversations} currentUserId={user?.id} onSelect={handleSelect} selectedId={selected?.contactId} search={search} setSearch={setSearch} />
        </div>
      )}

      {showThread && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <AnimatePresence mode="wait">
            {selected ? (
              <motion.div key={selected.contactId} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.18 }} style={{ height: '100%' }}>
                <ChatThread contactId={selected.contactId} contact={selected.contact} currentUser={user} onBack={() => setSelected(null)} />
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', background: '#F8F9FB' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,107,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MessageCircle size={36} color="rgba(255,107,0,0.35)" />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: '#111', letterSpacing: '1px', fontSize: '1rem', marginBottom: '6px' }}>VOS MESSAGES</p>
                  <p style={{ color: 'rgba(0,0,0,0.38)', fontSize: '0.84rem' }}>Sélectionnez une conversation à gauche</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
